import { decodePageSnapshotBody } from "@repo/adapter-core/page-snapshot-storage";
import { supabase } from "@/integrations/supabase/client";
import type { Property } from "./propertyDatabase";
import type { ValuationRun } from "./valuations";

export interface PropertyAsset {
  id: string;
  property_snapshot_id: string;
  asset_type: "photo" | "floorplan" | "pdf" | "document";
  storage_bucket: string;
  storage_path: string;
  source_url: string;
  mime_type: string | null;
  index_in_listing: number | null;
  captured_at: string;
}

export async function fetchPropertyAssets(snapshotIds: string[]): Promise<PropertyAsset[]> {
  const assets: PropertyAsset[] = [];
  for (let i = 0; i < snapshotIds.length; i += 100) {
    const ids = snapshotIds.slice(i, i + 100);
    for (let offset = 0; ; offset += 500) {
      const { data, error } = await (supabase as any)
        .from("property_assets")
        .select(
          "id,property_snapshot_id,asset_type,storage_bucket,storage_path,source_url,mime_type,index_in_listing,captured_at",
        )
        .in("property_snapshot_id", ids)
        .order("captured_at", { ascending: false })
        .order("id")
        .range(offset, offset + 499);
      if (error) throw error;
      assets.push(...(data ?? []));
      if (!data || data.length < 500) break;
    }
  }
  return assets;
}

export async function signStoredArtifact(bucket: string, path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60);
  if (error || !data?.signedUrl) throw error ?? new Error("No signed URL returned");
  return data.signedUrl;
}

/** A `page_snapshots` row's pointer to its stored body. */
export interface StoredPageRef {
  storage_bucket: string;
  storage_path: string;
  content_type?: string | null;
  content_encoding?: string | null;
}

/** Download a stored page and decode it; reads gzip and legacy uncompressed objects alike. */
export async function downloadStoredPage(page: StoredPageRef): Promise<string> {
  const { data, error } = await supabase.storage
    .from(page.storage_bucket)
    .download(page.storage_path);
  if (error || !data) throw error ?? new Error("Stored page unavailable");
  return decodePageSnapshotBody(data, page.content_encoding);
}

const escapeAttribute = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Wrap scraped HTML so it renders in a sandboxed iframe: no scripts and an opaque origin. The
 * page opens from a blob: URL, which shares the dashboard's origin, so scraped scripts must not
 * run there.
 */
export function sandboxedPageDocument(html: string, title: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeAttribute(title)}</title><style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style></head><body><iframe sandbox srcdoc="${escapeAttribute(html)}"></iframe></body></html>`;
}

/** Open a stored page in a new tab: HTML rendered in a sandbox, anything else as plain text. */
export async function openStoredPage(page: StoredPageRef): Promise<void> {
  const text = await downloadStoredPage(page);
  const isHtml = (page.content_type ?? "text/html").includes("html");
  const blob = isHtml
    ? new Blob([sandboxedPageDocument(text, page.storage_path)], { type: "text/html" })
    : new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function findPropertySuburb(
  property: Pick<Property, "suburb" | "state" | "postcode">,
): Promise<string | null> {
  if (!property.suburb || !property.state || !property.postcode) return null;
  const { data, error } = await (supabase as any)
    .from("suburbs")
    .select("id,locality,suburb_name")
    .eq("state", property.state)
    .eq("postcode", property.postcode)
    .limit(100);
  if (error) throw error;
  const name = property.suburb.trim().toLocaleLowerCase();
  const matches = (data ?? []).filter(
    (row: { locality: string; suburb_name: string }) =>
      row.locality?.trim().toLocaleLowerCase() === name ||
      row.suburb_name?.trim().toLocaleLowerCase() === name,
  );
  return matches.length === 1 ? matches[0].id : null;
}

export async function fetchLatestPropertySuburbPv(suburbId: string): Promise<ValuationRun | null> {
  const { data: algorithm, error: algorithmError } = await (supabase as any)
    .from("valuation_algorithms")
    .select("id")
    .eq("slug", "pv_suburb_movements_monthly_v1")
    .maybeSingle();
  if (algorithmError) throw algorithmError;
  if (!algorithm) return null;
  const { data, error } = await (supabase as any)
    .from("valuation_runs")
    .select("*")
    .eq("suburb_id", suburbId)
    .eq("algorithm_id", algorithm.id)
    .eq("status", "completed")
    .is("bedrooms", null)
    .is("bathrooms", null)
    .is("parking", null)
    .order("completed_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}
